"use client";

import { useState } from "react";
import { SelectField, TextField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  INPUT,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { codeColourFor } from "@/components/tools/qualitative-text/coding-colours";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type CodeCount,
  codePath,
  codeTree,
  type Project,
} from "@/lib/tool-kit/qualitative-text/coding";

interface CodebookStrings {
  codebookHeading: string;
  codebookHint: string;
  newCodeLabel: string;
  newCodePlaceholder: string;
  parentLabel: string;
  noParent: string;
  addCode: string;
  activeCode: string;
  rename: string;
  save: string;
  cancel: string;
  remove: string;
  countFormat: string;
  noCodes: string;
  removeCodeConfirm: string;
  renameLabel: string;
}

export function CodingCodebook({
  project,
  counts,
  activeId,
  strings,
  onActive,
  onAdd,
  onRename,
  onRemove,
}: {
  project: Project;
  counts: Map<string, CodeCount>;
  activeId: string | null;
  strings: CodebookStrings;
  onActive: (id: string) => void;
  onAdd: (name: string, parentId: string | null) => boolean;
  onRename: (id: string, name: string) => void;
  onRemove: (id: string) => void;
}) {
  const fmt = useToolNumber();
  const [name, setName] = useState("");
  const [parent, setParent] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [confirming, setConfirming] = useState<string | null>(null);
  const tree = codeTree(project);

  function submit() {
    if (onAdd(name, parent === "" ? null : parent)) {
      setName("");
    }
  }

  return (
    <div className="space-y-4">
      <h3 className={WIDGET_H3}>{strings.codebookHeading}</h3>
      <p className={HINT}>{strings.codebookHint}</p>
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          id="qc-new-code"
          label={strings.newCodeLabel}
          onChange={setName}
          placeholder={strings.newCodePlaceholder}
          value={name}
        />
        <SelectField
          id="qc-parent"
          label={strings.parentLabel}
          onChange={setParent}
          options={[
            { value: "", label: strings.noParent },
            ...tree.map(({ code }) => ({
              value: code.id,
              label: codePath(project, code.id),
            })),
          ]}
          value={parent}
        />
        <button
          className={BUTTON_SECONDARY}
          disabled={name.trim() === ""}
          type="submit"
        >
          {strings.addCode}
        </button>
      </form>
      {tree.length === 0 ? (
        <p className={HINT}>{strings.noCodes}</p>
      ) : (
        <ul className="space-y-1">
          {tree.map(({ code, depth }) => {
            const count = counts.get(code.id);
            const active = activeId === code.id;
            return (
              <li
                className={`rounded-lg border p-2 ${active ? "border-primary bg-primary/5" : "border-border bg-card"}`}
                key={code.id}
                style={{ marginLeft: `${depth * 16}px` }}
              >
                {editing === code.id ? (
                  <form
                    className="flex flex-wrap items-center gap-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      onRename(code.id, draft);
                      setEditing(null);
                    }}
                  >
                    <input
                      aria-label={strings.renameLabel}
                      className={`${INPUT} flex-1`}
                      onChange={(event) => setDraft(event.target.value)}
                      value={draft}
                    />
                    <button className={BUTTON_SMALL} type="submit">
                      {strings.save}
                    </button>
                    <button
                      className={BUTTON_SMALL}
                      onClick={() => setEditing(null)}
                      type="button"
                    >
                      {strings.cancel}
                    </button>
                  </form>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={`h-3 w-3 shrink-0 rounded-full ${codeColourFor(project, code.id).swatch}`}
                    />
                    <label className="flex flex-1 cursor-pointer items-center gap-2 text-foreground text-sm">
                      <input
                        checked={active}
                        className="accent-primary"
                        name="qc-active-code"
                        onChange={() => onActive(code.id)}
                        type="radio"
                      />
                      <span className="font-medium">{code.name}</span>
                      <span className="text-muted-foreground text-xs">
                        {t(strings.countFormat, {
                          direct: fmt(count?.direct ?? 0),
                          total: fmt(count?.total ?? 0),
                        })}
                      </span>
                    </label>
                    <button
                      className={BUTTON_SMALL}
                      onClick={() => {
                        setEditing(code.id);
                        setDraft(code.name);
                      }}
                      type="button"
                    >
                      {strings.rename}
                    </button>
                    {confirming === code.id ? (
                      <button
                        className={BUTTON_SMALL}
                        onClick={() => {
                          onRemove(code.id);
                          setConfirming(null);
                        }}
                        type="button"
                      >
                        {strings.removeCodeConfirm}
                      </button>
                    ) : (
                      <button
                        className={BUTTON_SMALL}
                        onClick={() => setConfirming(code.id)}
                        type="button"
                      >
                        {strings.remove}
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
